// Course header component

import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { StatusType } from 'types'
import { CourseType } from 'types'
import { EVCourseIcon, StatusIcon } from 'icons/EVIcons'
import { FormattedMessage } from 'react-intl'
import { getCourseTypeLabel } from 'utils/EVUtils/courseUtils/getCourseTypeLabel'
import { LightTooltip } from 'routes/Dashboard/style'

type Props = {
  primaryText: string
  completedDate?: string | null
  status?: StatusType
  courseType: CourseType
  isAvailable: boolean
  isLocked: boolean
  isBookingSession?: boolean
  courseCode?: string
  showSelectButton?: boolean
  onSelect?: () => void
  isSelectLoading?: boolean
  hasCannotPerformActionError?: boolean
}

export default function CourseHeader({
  primaryText,
  status,
  courseType,
  courseCode,
  isAvailable,
  isLocked,
  isBookingSession,
  showSelectButton,
  onSelect,
  isSelectLoading,
  hasCannotPerformActionError,
}: Props) {
  const isMediumScreen = useMediaQuery('(min-width: 768px)')
  const isSmallScreen = useMediaQuery('(min-width: 768px)')
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      width="100%"
      minWidth={isMediumScreen ? 650 : isSmallScreen ? 'auto' : 375}
    >
      <Box minWidth={70} display="flex" justifyContent="center">
        <EVCourseIcon courseType={courseType} />
      </Box>
      <Stack flex={1} alignItems="flex-start">
        <Typography
          data-testid="primary-text"
          color={isBookingSession ? '#1e1e1e' : '#525252'}
          textTransform="uppercase"
          textAlign="left"
          fontSize={isBookingSession ? 18 : 16}
          letterSpacing={isBookingSession ? '1.8px' : 'inherit'}
        >
          {primaryText}
        </Typography>
        {!isBookingSession && courseCode && (
          <Typography
            data-testid="primary-text"
            color="rgb(151,151,151)"
            fontSize={14}
            textAlign="left"
          >
            {courseCode}
          </Typography>
        )}
        {isBookingSession && courseCode && (
          <Typography
            data-testid="primary-text"
            color="rgb(151,151,151)"
            fontSize={14}
            textAlign="left"
          >
            {courseCode}
          </Typography>
        )}
        {isBookingSession && (
          <Typography
            data-testid="primary-text"
            color="#525252"
            textAlign="left"
            fontSize={16}
          >
            {getCourseTypeLabel(courseType)}
          </Typography>
        )}
      </Stack>
      {showSelectButton ? (
        <LightTooltip
          title={
            hasCannotPerformActionError ? (
              <Box>
                <p>
                  <FormattedMessage id={'evLevel.cannotPerformAction'} />
                </p>
              </Box>
            ) : (
              ''
            )
          }
          arrow
          open={!!hasCannotPerformActionError}
          disableHoverListener={!!hasCannotPerformActionError}
          disableFocusListener={!!hasCannotPerformActionError}
          disableTouchListener={!!hasCannotPerformActionError}
        >
          <span>
            <Button
              color="info"
              variant="outlined"
              size="small"
              disabled={isSelectLoading || hasCannotPerformActionError}
              onClick={e => {
                e.stopPropagation()
                onSelect?.()
              }}
              sx={{ whiteSpace: 'nowrap', minWidth: 80 }}
            >
              {isSelectLoading ? (
                <CircularProgress size={18} />
              ) : (
                <FormattedMessage id="app.labels.select" />
              )}
            </Button>
          </span>
        </LightTooltip>
      ) : null}
      {!showSelectButton && status ? (
        <StatusIcon
          status={status}
          size="big"
          isAvailable={isAvailable}
          isLocked={isLocked}
        />
      ) : null}
    </Stack>
  )
}
